/**
 * `.zwdoc` on-disk format (#248): JSON Lines.
 *
 *   line 1      header (`ZwDocHeader`, fixed key order)
 *   line 2..n   one top-level ProseMirror block (`Node.toJSON()`)
 *   trailing newline
 *
 * One block per line keeps git diffs and the history panel at paragraph
 * granularity. Blocks always go through the ProseMirror schema on the way in
 * and out, so output is canonical (attr order, joined text runs) regardless of
 * where the JSON came from.
 */

import { Node as PMNode } from "prosemirror-model";
import { err, ok, type Result } from "../result";
import type {
  ZwDocCommentMessage,
  ZwDocCommentThread,
  ZwDocEntityFields,
  ZwDocFile,
  ZwDocHeader,
  ZwDocKind,
} from "./generated/zwDoc";
import { zwdocSchema } from "./schema";

/** A parsed document: typed header + validated ProseMirror doc. */
export type ZwDoc = {
  header: ZwDocHeader;
  doc: PMNode;
};

export type ZwDocParseError = {
  /** 1-based line in the file; 0 when the file as a whole is invalid. */
  line: number;
  message: string;
};

/** Every `.zwdoc` starts with this: the header is line 1 and `zw` is its first key. */
export const ZWDOC_PREFIX = '{"zw":';

/** True when `text` is `.zwdoc` content rather than legacy Markdown. */
export function isZwDocText(text: string): boolean {
  return text.startsWith(ZWDOC_PREFIX);
}

/**
 * `{…}` that is not a `.zwdoc` header (maps, stray JSON). Must never be
 * persisted to a `.zwdoc` path; Markdown bodies are still allowed until
 * convert-on-open.
 */
export function isForeignJsonDocBody(text: string): boolean {
  const trimmed = text.trimStart();
  return trimmed.startsWith("{") && isZwDocText(trimmed) === false;
}

export function emptyZwDocHeader(): ZwDocHeader {
  return { zw: 1, synopsis: "", kind: null, fields: null, comments: {} };
}

export function emptyZwDocNode(): PMNode {
  return zwdocSchema.node("doc", null, [zwdocSchema.node("paragraph")]);
}

/**
 * Text of a new `.zwdoc` file (scene, note, Database entry, chapter / act
 * index): optional park / act `kind`, and optionally a level-1 title heading
 * followed by an empty paragraph. Notes and Database entries create blank
 * (no title); scenes and chapters do too. Callers may still pass `title`
 * when seeding a heading is intentional.
 */
export function newZwDocText(
  options: { kind?: ZwDocKind; title?: string } = {},
): string {
  const s = zwdocSchema;
  const title = options.title?.trim() ?? "";
  const doc =
    title === ""
      ? emptyZwDocNode()
      : s.node("doc", null, [
          s.node("heading", { level: 1 }, [s.text(title)]),
          s.node("paragraph"),
        ]);
  return serializeZwDoc({
    header: { ...emptyZwDocHeader(), kind: options.kind ?? null },
    doc,
  });
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    Array.isArray(value) === false
  );
}

/** Validate one block's JSON against the schema and return it as a node. */
function blockFromJson(json: unknown): Result<PMNode, string> {
  try {
    const node = zwdocSchema.nodeFromJSON(json);
    if (node.type.spec.group !== "block") {
      return err(`Expected a block node, got ${node.type.name}`);
    }
    node.check();
    return ok(node);
  } catch (error) {
    return err(errorMessage(error));
  }
}

function docFromBlocks(blocks: readonly PMNode[]): Result<PMNode, string> {
  try {
    const doc =
      blocks.length === 0
        ? emptyZwDocNode()
        : zwdocSchema.node("doc", null, [...blocks]);
    doc.check();
    return ok(doc);
  } catch (error) {
    return err(errorMessage(error));
  }
}

/** Wire JSON (e.g. converter output) → validated `ZwDoc`. */
export function zwDocFromFile(file: ZwDocFile): Result<ZwDoc, ZwDocParseError> {
  const header = decodeZwDocHeader(file.header);
  if (header.ok === false) return err({ line: 1, message: header.error });

  const blocks: PMNode[] = [];
  for (const [index, json] of file.blocks.entries()) {
    const block = blockFromJson(json);
    if (block.ok === false)
      return err({ line: index + 2, message: block.error });
    blocks.push(block.value);
  }
  const doc = docFromBlocks(blocks);
  if (doc.ok === false) return err({ line: 0, message: doc.error });
  return ok({ header: header.value, doc: doc.value });
}

/** `ZwDoc` → wire JSON, canonicalized through the schema. */
export function zwDocToFile(zwdoc: ZwDoc): ZwDocFile {
  const blocks: ZwDocFile["blocks"][number][] = [];
  zwdoc.doc.forEach((block) => {
    blocks.push(block.toJSON() as ZwDocFile["blocks"][number]);
  });
  const [first, ...rest] = blocks;
  return {
    header: canonicalHeader(zwdoc.header),
    blocks:
      typeof first === "undefined"
        ? [emptyZwDocNode().child(0).toJSON() as ZwDocFile["blocks"][number]]
        : [first, ...rest],
  };
}

export function serializeZwDoc(zwdoc: ZwDoc): string {
  const file = zwDocToFile(zwdoc);
  const lines = [file.header, ...file.blocks].map((value) =>
    JSON.stringify(value),
  );
  return `${lines.join("\n")}\n`;
}

export function parseZwDoc(text: string): Result<ZwDoc, ZwDocParseError> {
  const lines = text.split("\n").map((line) => line.replace(/\r$/, ""));
  const headerLine = lines[0];
  if (typeof headerLine === "undefined" || headerLine.trim() === "") {
    return err({ line: 1, message: "Missing header line" });
  }

  let headerJson: unknown;
  try {
    headerJson = JSON.parse(headerLine);
  } catch (error) {
    return err({ line: 1, message: errorMessage(error) });
  }
  const header = decodeZwDocHeader(headerJson);
  if (header.ok === false) return err({ line: 1, message: header.error });

  const blocks: PMNode[] = [];
  for (const [index, line] of lines.slice(1).entries()) {
    if (line.trim() === "") continue;
    const lineNumber = index + 2;
    let json: unknown;
    try {
      json = JSON.parse(line);
    } catch (error) {
      return err({ line: lineNumber, message: errorMessage(error) });
    }
    const block = blockFromJson(json);
    if (block.ok === false)
      return err({ line: lineNumber, message: block.error });
    blocks.push(block.value);
  }

  const doc = docFromBlocks(blocks);
  if (doc.ok === false) return err({ line: 0, message: doc.error });
  return ok({ header: header.value, doc: doc.value });
}

/** Read just the header line (cheap: the body isn't parsed). */
export function readZwDocHeader(text: string): Result<ZwDocHeader, string> {
  const end = text.indexOf("\n");
  const line = (end === -1 ? text : text.slice(0, end)).replace(/\r$/, "");
  try {
    return decodeZwDocHeader(JSON.parse(line));
  } catch (error) {
    return err(errorMessage(error));
  }
}

/** Replace the header line, leaving the body bytes untouched. */
export function withZwDocHeader(text: string, header: ZwDocHeader): string {
  const end = text.indexOf("\n");
  const body = end === -1 ? "\n" : text.slice(end);
  return `${JSON.stringify(canonicalHeader(header))}${body}`;
}

let lastParsed: {
  text: string;
  result: Result<ZwDoc, ZwDocParseError>;
} | null = null;

/**
 * `parseZwDoc` with a one-entry memo, for UI that re-reads the same text from
 * several places per keystroke (comment margin, layout, save gc).
 */
export function parseZwDocCached(text: string): Result<ZwDoc, ZwDocParseError> {
  if (lastParsed?.text === text) return lastParsed.result;
  const result = parseZwDoc(text);
  lastParsed = { text, result };
  return result;
}

// --- Header decode / canonical key order ---------------------------------

const DOC_KINDS: readonly ZwDocKind[] = ["scene", "chapter", "act"];

function decodeCommentMessage(
  value: unknown,
): Result<ZwDocCommentMessage, string> {
  if (isRecord(value) === false)
    return err("Comment message must be an object");
  const { id, body, createdAt, authorId, authorName } = value;
  if (typeof id !== "string" || typeof body !== "string") {
    return err("Comment message needs string id and body");
  }
  if (typeof createdAt !== "string") {
    return err("Comment message needs string createdAt");
  }
  return ok({
    id,
    body,
    createdAt,
    ...(typeof authorId === "string" ? { authorId } : {}),
    ...(typeof authorName === "string" ? { authorName } : {}),
  });
}

function decodeCommentThread(
  value: unknown,
): Result<ZwDocCommentThread, string> {
  if (isRecord(value) === false) return err("Comment thread must be an object");
  const { createdAt, resolved, messages } = value;
  if (typeof createdAt !== "string" || typeof resolved !== "boolean") {
    return err("Comment thread needs string createdAt and boolean resolved");
  }
  if (Array.isArray(messages) === false)
    return err("Comment thread needs messages");
  const decoded: ZwDocCommentMessage[] = [];
  for (const message of messages) {
    const result = decodeCommentMessage(message);
    if (result.ok === false) return result;
    decoded.push(result.value);
  }
  return ok({ createdAt, resolved, messages: decoded });
}

export function decodeZwDocHeader(value: unknown): Result<ZwDocHeader, string> {
  if (isRecord(value) === false) return err("Header must be an object");
  if (value.zw !== 1)
    return err(`Unsupported zwdoc version: ${String(value.zw)}`);
  if (typeof value.synopsis !== "string")
    return err("Header synopsis must be a string");

  const kind = value.kind;
  if (kind !== null && DOC_KINDS.includes(kind as ZwDocKind) === false) {
    return err(`Invalid header kind: ${String(kind)}`);
  }
  const fields = value.fields;
  if (fields !== null && isRecord(fields) === false) {
    return err("Header fields must be an object or null");
  }
  if (isRecord(value.comments) === false)
    return err("Header comments must be an object");

  const comments: Record<string, ZwDocCommentThread> = {};
  for (const [id, thread] of Object.entries(value.comments)) {
    const result = decodeCommentThread(thread);
    if (result.ok === false) return err(`Comment ${id}: ${result.error}`);
    comments[id] = result.value;
  }

  return ok({
    zw: 1,
    synopsis: value.synopsis,
    kind: kind as ZwDocKind | null,
    // Entity fields are owned and validated by Rust serde (#253).
    fields: fields as ZwDocEntityFields | null,
    comments,
  });
}

/** Rebuild objects in schema key order so equal headers serialize identically. */
function canonicalHeader(header: ZwDocHeader): ZwDocHeader {
  const comments: Record<string, ZwDocCommentThread> = {};
  for (const [id, thread] of Object.entries(header.comments)) {
    comments[id] = {
      createdAt: thread.createdAt,
      resolved: thread.resolved,
      messages: thread.messages.map((message) => ({
        id: message.id,
        body: message.body,
        createdAt: message.createdAt,
        ...(typeof message.authorId === "string"
          ? { authorId: message.authorId }
          : {}),
        ...(typeof message.authorName === "string"
          ? { authorName: message.authorName }
          : {}),
      })),
    };
  }
  return {
    zw: 1,
    synopsis: header.synopsis,
    kind: header.kind,
    fields:
      header.fields === null ? null : canonicalEntityFields(header.fields),
    comments,
  };
}

const ENTITY_FIELD_ORDER: readonly (keyof ZwDocEntityFields)[] = [
  "type",
  "aka",
  "role",
  "plural",
  "adjective",
  "summary",
  "image",
  "tags",
  "color",
  "status",
  "custom",
  "links",
  "arc",
];

function canonicalEntityFields(fields: ZwDocEntityFields): ZwDocEntityFields {
  const out: Record<string, unknown> = {};
  for (const key of ENTITY_FIELD_ORDER) {
    const value = fields[key];
    if (typeof value !== "undefined") out[key] = value;
  }
  return out as ZwDocEntityFields;
}
